import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, ArrowRight, User, Building, Camera, Target, CheckCircle, AlertCircle, Upload } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://matchmaking.mobivent.fr/api';

const RegisterPage = () => {
  const { event } = useEvent();
  const { register } = useAuth();
  const navigate = useNavigate();

  // Steps
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  // Form data
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    password: '',
    company: '',
    fonction: '',
    profilePicture: null,
    categories: [],
    activities: []
  });

  // Categories & Activities from Airtable
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);

  // Load Categories and Activities from Airtable
  useEffect(() => {
    if (currentStep === 4) {
      loadCategoriesAndActivities();
    }
  }, [currentStep]);

  const loadCategoriesAndActivities = async () => {
    setLoadingCategories(true);
    try {
      // Charger les catégories
      const categoriesRes = await fetch(`${API_BASE_URL}/categories`);
      const categoriesData = await categoriesRes.json();
      if (categoriesData.success) {
        setCategories(categoriesData.data);
      }

      // Charger les activités
      const activitiesRes = await fetch(`${API_BASE_URL}/activities`);
      const activitiesData = await activitiesRes.json();
      if (activitiesData.success) {
        setActivities(activitiesData.data);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        setError('Photo size must be less than 5MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, profilePicture: file }));
      
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => {
      const newCategories = prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId];
      return { ...prev, categories: newCategories };
    });
  };

  const toggleActivity = (activityId) => {
    setFormData(prev => {
      const newActivities = prev.activities.includes(activityId)
        ? prev.activities.filter(id => id !== activityId)
        : [...prev.activities, activityId];
      return { ...prev, activities: newActivities };
    });
  };

  const validateStep = (step) => {
    setError('');
    
    switch (step) {
      case 1:
        if (!formData.firstname || !formData.lastname) {
          setError('Please enter your first and last name');
          return false;
        }
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        if (!formData.phone) {
          setError('Please enter your phone number');
          return false;
        }
        if (!formData.password || formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          return false;
        }
        return true;
      
      case 2:
        if (!formData.company) {
          setError('Please enter your company name');
          return false;
        }
        if (!formData.fonction) {
          setError('Please enter your job title');
          return false;
        }
        return true;
      
      case 3:
        // Photo is optional
        return true;
      
      case 4:
        if (formData.activities.length === 0) {
          setError('Please select at least one activity');
          return false;
        }
        return true;
      
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    setError('');

    try {
      // 1. Register user
      const registerData = {
        email: formData.email,
        firstname: formData.firstname,
        lastname: formData.lastname,
        company: formData.company,
        phone: formData.phone,
        fonction: formData.fonction,
        password: formData.password,
        event_id: event.id,
        event_name: event.name
      };

      await register(registerData);

      // 2. Rediriger vers la page de vérification du code
      navigate(`/event/${event.slug}/verify`, { 
        state: { 
          email: formData.email, 
          action: 'register',
          profileData: {
            profilePicture: formData.profilePicture,
            categories: formData.categories,
            activities: formData.activities
          }
        } 
      });

    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEvent = () => {
    navigate(`/event/${event.slug}`);
  };

  // Filter activities by selected categories
  const filteredActivities = formData.categories.length > 0
    ? activities.filter(activity => 
        formData.categories.includes(activity.category_id)
      )
    : activities;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={currentStep === 1 ? handleBackToEvent : handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full">
          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Event Logo */}
            {event.logo && (
              <div className="text-center mb-6">
                <img 
                  src={event.logo} 
                  alt={event.name}
                  className="max-h-16 object-contain mx-auto"
                />
              </div>
            )}

            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-700">
                  Step {currentStep} of {totalSteps}
                </span>
                <span className="text-sm text-gray-600">
                  {Math.round((currentStep / totalSteps) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="text-blue-600" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Personal Information
                  </h2>
                  <p className="text-gray-600">
                    Let's start with your basic information
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.firstname}
                        onChange={(e) => handleChange('firstname', e.target.value)}
                        placeholder="John"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.lastname}
                        onChange={(e) => handleChange('lastname', e.target.value)}
                        placeholder="Doe"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="john.doe@company.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+33 6 12 34 56 78"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Minimum 6 characters
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Company Information */}
            {currentStep === 2 && (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building className="text-purple-600" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Company Information
                  </h2>
                  <p className="text-gray-600">
                    Tell us about your company
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      placeholder="Acme Corporation"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Job Title / Function *
                    </label>
                    <input
                      type="text"
                      value={formData.fonction}
                      onChange={(e) => handleChange('fonction', e.target.value)}
                      placeholder="CEO, Sales Manager, Engineer..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Profile Picture */}
            {currentStep === 3 && (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Camera className="text-green-600" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Profile Picture
                  </h2>
                  <p className="text-gray-600">
                    Add a professional photo (optional)
                  </p>
                </div>

                <div className="text-center">
                  {photoPreview ? (
                    <div className="mb-6">
                      <img 
                        src={photoPreview} 
                        alt="Preview"
                        className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-blue-100"
                      />
                      <button
                        onClick={() => {
                          setFormData(prev => ({ ...prev, profilePicture: null }));
                          setPhotoPreview(null);
                        }}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Remove photo
                      </button>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <User className="text-gray-400" size={48} />
                      </div>
                    </div>
                  )}

                  <label className="inline-block cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                    <div className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition inline-flex items-center gap-2">
                      <Upload size={20} />
                      {photoPreview ? 'Change Photo' : 'Upload Photo'}
                    </div>
                  </label>

                  <p className="mt-4 text-sm text-gray-500">
                    Maximum file size: 5MB
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Categories & Activities */}
            {currentStep === 4 && (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="text-orange-600" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Your Interests
                  </h2>
                  <p className="text-gray-600">
                    Select categories and activities that match your profile
                  </p>
                </div>

                {loadingCategories ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading categories...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Categories */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Categories
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {categories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => toggleCategory(category.id)}
                            className={`px-4 py-3 rounded-lg border-2 transition text-left ${
                              formData.categories.includes(category.id)
                                ? 'bg-blue-50 border-blue-500 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            {category.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Activities */}
                    {formData.categories.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Activities * (Select at least one)
                        </label>
                        <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto p-2">
                          {filteredActivities.map((activity) => (
                            <button
                              key={activity.id}
                              onClick={() => toggleActivity(activity.id)}
                              className={`px-3 py-2 rounded-lg border-2 transition text-sm text-left ${
                                formData.activities.includes(activity.id)
                                  ? 'bg-green-50 border-green-500 text-green-700'
                                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                              }`}
                            >
                              {activity.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {formData.categories.length === 0 && (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-600">
                          Please select at least one category to see activities
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Summary */}
            {currentStep === 5 && (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Review Your Information
                  </h2>
                  <p className="text-gray-600">
                    Please verify your details before submitting
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Personal Information</h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-600">Name:</span> {formData.firstname} {formData.lastname}</p>
                      <p><span className="text-gray-600">Email:</span> {formData.email}</p>
                      <p><span className="text-gray-600">Phone:</span> {formData.phone}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Company</h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-600">Company:</span> {formData.company}</p>
                      <p><span className="text-gray-600">Job Title:</span> {formData.fonction}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Profile Picture</h3>
                    <p className="text-sm text-gray-600">
                      {formData.profilePicture ? 'Photo uploaded ✓' : 'No photo uploaded'}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Activities</h3>
                    <p className="text-sm text-gray-600">
                      {formData.activities.length} activities selected
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-8 flex gap-3">
              {currentStep > 1 && (
                <button
                  onClick={handleBack}
                  disabled={loading}
                  className="flex-1 bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
              )}

              {currentStep < totalSteps ? (
                <button
                  onClick={handleNext}
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span>Next</span>
                  <ArrowRight size={20} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Creating account...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      <span>Create Account</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">
            Powered by <span className="font-semibold">Mobivent</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RegisterPage;